import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ArrowLeft } from "lucide-react";
import { solutions } from "../mock";

const SolutionDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const solution = solutions.find((s) => s.slug === slug);

  useEffect(() => window.scrollTo(0, 0), [slug]);

  if (!solution) {
    return (
      <div className="min-h-screen bg-[#050f24] flex flex-col items-center justify-center text-white gap-6">
        <h1 className="text-4xl font-bold" style={{ fontFamily: "Archivo, sans-serif" }}>Solution not found</h1>
        <Link to="/" className="text-white inline-flex items-center gap-2"><ArrowLeft size={16} /> Back to home</Link>
      </div>
    );
  }

  return (
    <main className="bg-[#050f24] min-h-screen">
      <section className="relative h-[62vh] min-h-[440px] overflow-hidden">
        <img src={solution.image} alt={solution.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050f24] via-[#050f24]/50 to-[#050f24]/40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-16">
          <p className="text-white text-sm font-semibold tracking-wide uppercase mb-3">What We Do</p>
          <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold max-w-3xl leading-[1.08]" style={{ fontFamily: "Archivo, sans-serif" }}>
            {solution.title}
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1.4fr_1fr] gap-14">
        <div>
          <h2 className="text-white text-3xl font-bold mb-6" style={{ fontFamily: "Archivo, sans-serif" }}>Overview</h2>
          <p className="text-white/75 text-lg leading-relaxed mb-10">{solution.overview}</p>
          <h3 className="text-white text-xl font-bold mb-5" style={{ fontFamily: "Archivo, sans-serif" }}>What we offer</h3>
          <ul className="space-y-3.5">
            {solution.offerings.map((o) => (
              <li key={o} className="flex items-start gap-3 text-white/80">
                <CheckCircle2 size={19} className="text-white mt-0.5 flex-shrink-0" />
                <span>{o}</span>
              </li>
            ))}
          </ul>
        </div>

        <aside className="lg:sticky lg:top-28 self-start bg-[#0b2044] border border-white/10 rounded-2xl p-8">
          <h3 className="text-white text-xl font-bold mb-3" style={{ fontFamily: "Archivo, sans-serif" }}>
            Talk to an expert
          </h3>
          <p className="text-white/60 text-[15px] leading-relaxed mb-6">
            Tell us about your {solution.title.toLowerCase()} goals and we'll design a roadmap for you.
          </p>
          <button
            onClick={() => navigate("/contact")}
            className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-[#d7e3f4] text-[#04122e] text-sm font-bold px-6 py-3 rounded-full transition-colors"
          >
            Get in Touch <ArrowRight size={15} />
          </button>
        </aside>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <h3 className="text-white text-2xl font-bold mb-8" style={{ fontFamily: "Archivo, sans-serif" }}>Explore more solutions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {solutions.filter((s) => s.slug !== slug).slice(0, 4).map((s) => (
            <div
              key={s.slug}
              onClick={() => navigate(`/solutions/${s.slug}`)}
              className="group relative rounded-xl overflow-hidden cursor-pointer aspect-[4/3] ring-1 ring-white/5 hover:ring-white/40 transition-all"
            >
              <img src={s.image} alt={s.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050f24]/90 to-black/20 group-hover:from-[#050f24]/95 transition-colors" />
              <h4 className="absolute bottom-4 left-4 right-4 text-white font-semibold leading-tight">{s.title}</h4>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default SolutionDetail;
